import { Component, computed, inject, linkedSignal, Signal, signal, viewChild } from '@angular/core';
import { DashboardContainerComponent } from '../../layout/dashboard-container/dashboard-container.component';
import { FilterBarComponent } from '../../filter-bar/filter-bar.component';
import { Table } from '@cumlaude/metadata';
import { FilterPanelComponent } from '../../filter-panel/filter-panel.component';
import { BasicFilterExpression, DataService, ExportDataOptions, ExportFilter } from '../../services/data.service';
import { combineLatest, map, Observable, switchMap } from 'rxjs';
import { createSelection } from '../../shared/dashboard/base-dashboard/base-dashboard-config';
import { AanwezigheidPerLesuurComponent } from './aanwezigheid-per-lesuur/aanwezigheid-per-lesuur.component';
import { AanwezigheidPerSchooldagComponent } from './aanwezigheid-per-schooldag/aanwezigheid-per-schooldag.component';
import { AanwezigheidRedenenComponent } from './aanwezigheid-redenen/aanwezigheid-redenen.component';
import { AanwezigheidPerMaandComponent } from './aanwezigheid-per-maand/aanwezigheid-per-maand.component';
import { Dashboard } from '../../shared/dashboard/base-dashboard/dashboard';
import { CustomExportItem, ExportOptions } from '../../services/export.service';
import { LegendaComponent } from '../../legenda/legenda.component';
import { ExportablePanel } from '../../services/exportable';
import { getAfgeslotenMaandenFilter } from '../../services/schoolaanwezigheid';
import { FilterName } from '../../services/filter-name';

@Component({
	selector: 'app-aanwezigheid-statistieken',
	templateUrl: './aanwezigheid-statistieken.component.html',
	styleUrls: ['./aanwezigheid-statistieken.component.scss'],
	imports: [
		DashboardContainerComponent,
		FilterBarComponent,
		FilterPanelComponent,
		AanwezigheidPerMaandComponent,
		AanwezigheidPerLesuurComponent,
		AanwezigheidPerSchooldagComponent,
		AanwezigheidRedenenComponent,
		LegendaComponent,
	],
})
export class AanwezigheidStatistiekenComponent extends Dashboard {
	readonly factTable = Table.fac_aw_aanwezigheid;

	readonly defaultFilters: FilterName[] = [
		'aw_nm_schooljaar',
		'aw_fk_lb.lb_co_brin',
		'aw_fk_vs.vs_nm_vestiging',
		'aw_fk_ilt.ilt_nm_niveau',
		'aw_nr_leerjaar',
		'aw_nm_klas',
		'aw_fun_nm_abs_reden',
		'aw_fun_nm_vak',
	];

	// Signal voor dataRootSelection van het per-maand component
	readonly dataRootSelection = signal<ReturnType<typeof createSelection> | undefined>(undefined);

	readonly permanentFilters = computed(() => {
		const vestigingen = this.userService.schoolaanwezigheidVestigingen();
		return [
			new BasicFilterExpression(['aw_is_presentieregistratie_verwerkt'], 1),
			getAfgeslotenMaandenFilter(),
			new BasicFilterExpression(['aw_id_vestiging'], vestigingen, 'in'),
		];
	});

	readonly aanwezigheidPerMaand = viewChild.required(AanwezigheidPerMaandComponent);

	readonly aanwezigheidRedenen = viewChild.required(AanwezigheidRedenenComponent);

	readonly aanwezigheidPerSchooldag = viewChild.required(AanwezigheidPerSchooldagComponent);

	readonly aanwezigheidPerLesuur = viewChild.required(AanwezigheidPerLesuurComponent);

	private readonly dataService = inject(DataService);

	onDataRootSelectionChange(selection: ReturnType<typeof createSelection> | undefined) {
		this.dataRootSelection.set(selection);
	}

	override getExportData(options: ExportDataOptions): Observable<Blob> {
		return this.dataService.getAanwezigheidExportData(options);
	}

	override exportAsTable(exportOptions: ExportOptions, _title: string, filters: ExportFilter[]): Observable<Blob> {
		const exportPages = this.getCustomExportItemsAsArray()
			.filter(({ condition }) => condition())
			.map(({ component }) => component().exportAsTable(exportOptions, filters));
		return combineLatest(exportPages).pipe(
			map((pages) => {
				return {
					showDatum: exportOptions.showDatum,
					pages,
				};
			}),
			switchMap((tableExport) => this.dataService.getTableExport(tableExport))
		);
	}

	protected customExportItems = {
		aanwezigheidPerMaand: createCustomExportItem('Aanwezigheid per maand', this.aanwezigheidPerMaand),
		afwezigheidsredenen: createCustomExportItem('Afwezigheidsredenen', this.aanwezigheidRedenen),
		aanwezigheidPerSchooldag: createCustomExportItem('Gem. aanwezigheid per schooldag', this.aanwezigheidPerSchooldag),
		aanwezigheidPerLesuur: createCustomExportItem('Gem. aanwezigheid per lesuur', this.aanwezigheidPerLesuur),
	};

	protected getCustomExportItemsAsArray(): CustomExportItem[] {
		return Object.values(this.customExportItems);
	}
}

type CustomExportItemWithLoading = CustomExportItem & { setLoading: (loading: boolean) => void };

function createCustomExportItem(label: string, component: Signal<ExportablePanel>): CustomExportItemWithLoading {
	const loading = signal(false);
	return {
		label,
		condition: linkedSignal(() => !loading()),
		component,
		disabled: () => (loading() ? 'Bezig met laden...' : false),
		setLoading: (isLoading: boolean) => loading.set(isLoading),
	};
}
